import { useCallback, useEffect, useRef, useState } from 'react';
import type { BoardPayload } from '../game/leaderboard';
import { fetchBoard, saveHandle } from '../game/leaderboard';
import type { Player } from '../game/identity';
import { play } from '../game/sound';

interface Props {
  player: Player | null;
  onSignIn: () => void;
  onClose: () => void;
}

/**
 * All-time top scores, as an arcade attract screen.
 *
 * It floats over whatever was on screen rather than replacing it — the score
 * that just moved somebody up the board is still visible behind the board.
 *
 * A total is the sum of the BEST score on each level, so replaying can only
 * improve a standing. That is what makes replaying worth encouraging, and it
 * means the board describes what somebody can do rather than how many attempts
 * they have made.
 *
 * Appearing here is opt-in and pseudonymous: three characters, no name, no
 * address. Somebody who only wants to play never appears.
 */
export function Leaderboard({ player, onSignIn, onClose }: Props) {
  const [payload, setPayload] = useState<BoardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const next = await fetchBoard();
    setPayload(next);
    setDraft(next?.handle ?? '');
    setLoading(false);
  }, []);

  // Once, on opening, and only when there is actually a board to announce.
  const announced = useRef(false);
  useEffect(() => {
    if (announced.current || loading || (payload?.board.length ?? 0) === 0) return;
    announced.current = true;
    play('highScore');
  }, [loading, payload]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Escape closes it.
   *
   * Captured on the window rather than the panel so it works wherever focus
   * happens to be, including inside the handle field.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    const outcome = await saveHandle(draft);
    setBusy(false);
    if (!outcome.ok) {
      setMessage(outcome.error ?? 'Could not save that handle.');
      return;
    }
    setMessage(outcome.handle ? `ON THE BOARD AS ${outcome.handle}` : 'REMOVED FROM THE BOARD');
    await load();
  }, [draft, load]);

  const board = payload?.board ?? [];
  const you = payload?.you ?? null;
  const listedBelow = you && !board.some((row) => row.rank === you.rank);

  return (
    <div
      className="arcade"
      role="dialog"
      aria-modal="true"
      aria-label="All-time top scores"
      // A tap anywhere outside the board closes it. Checked against the panel
      // rather than the event target's class so a tap on any of the panel's
      // children — including the handle field — is never mistaken for a
      // dismissal.
      onPointerDown={(event) => {
        if (!panelRef.current?.contains(event.target as Node)) onClose();
      }}
    >
      <div className="arcade__panel" ref={panelRef}>
        <button
          type="button"
          className="arcade__close"
          aria-label="Close the leaderboard"
          onClick={onClose}
        >
          ×
        </button>

        <p className="arcade__eyebrow">★ high scores ★</p>
        <h2 className="arcade__title">TOP 20 ALL TIME</h2>

        {loading ? (
          <p className="arcade__note">LOADING…</p>
        ) : board.length === 0 ? (
          <p className="arcade__note">
            NO SCORES YET
            <br />
            <span className="arcade__small">CLAIM A HANDLE AND CLOSE A CASE</span>
          </p>
        ) : (
          <ol className="scoreboard">
            {board.map((row) => (
              <li
                key={`${row.rank}-${row.handle}`}
                className={`scoreboard__row${you && row.rank === you.rank ? ' scoreboard__row--you' : ''}`}
              >
                <span className="scoreboard__rank">{String(row.rank).padStart(2, '0')}</span>
                <span className="scoreboard__handle">{row.handle}</span>
                <span className="scoreboard__levels">
                  {row.levels} CASE{row.levels === 1 ? '' : 'S'}
                </span>
                <span className="scoreboard__total">{row.total}</span>
              </li>
            ))}
          </ol>
        )}

        {listedBelow && you && (
          <ol className="scoreboard scoreboard--you">
            <li className="scoreboard__row scoreboard__row--you">
              <span className="scoreboard__rank">{String(you.rank).padStart(2, '0')}</span>
              <span className="scoreboard__handle">{you.handle}</span>
              <span className="scoreboard__levels">
                {you.levels} CASE{you.levels === 1 ? '' : 'S'}
              </span>
              <span className="scoreboard__total">{you.total}</span>
            </li>
          </ol>
        )}

        <div className="arcade__entry">
          {player ? (
            <>
              <label className="arcade__label" htmlFor="handle">
                ENTER YOUR INITIALS
              </label>
              <div className="arcade__row">
                <input
                  id="handle"
                  className="arcade__input"
                  value={draft}
                  maxLength={3}
                  placeholder="AAA"
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setDraft(event.target.value.toUpperCase())}
                />
                <button
                  type="button"
                  className="arcade__go"
                  disabled={busy}
                  onClick={() => {
                    play('coin');
                    void submit();
                  }}
                >
                  {busy ? '…' : draft ? 'ENTER' : 'CLEAR'}
                </button>
              </div>
              <p className="arcade__small">
                Three characters. Only this is shown — never your name or email.
                Empty takes you off the board.
              </p>
              {message && (
                <p className="arcade__message" role="status">
                  {message}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="arcade__small">
                Sign in to claim a handle. The board is public either way.
              </p>
              <button type="button" className="arcade__go" onClick={onSignIn}>
                SIGN IN
              </button>
            </>
          )}
        </div>

        <p className="arcade__hint">Press ESC or tap outside to close</p>
      </div>
    </div>
  );
}
